import {useEffect, useState} from "react";

import moment from "moment";
import {useStore} from "../../../../../../hooks/useStore";
import {useFetch} from "../../../../../../hooks/useFetch";
import {date} from "yup";

export const useCourierWalletSubTab = ({
    courier, 
    canRender = false, 
    walletData, 
    selectedDateRange = [moment().subtract(7, "day"), moment()],
    onDateRangeChange = () => {}
}) => {
    const stores = useStore();
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedDailyTransactionId, setSelectedDailyTransactionId] = useState(null);
    const [dailyDetail, setDailyDetail] = useState(null);
    const [dailyDetailLoading, setDailyDetailLoading] = useState(false);

    const [dailyTransactions, setDailyTransactions] = useState([]);
    const [dailyTransactionsLoading, setDailyTransactionsLoading] = useState(false);
    const [wallet, setWallet] = useState(undefined);
    const [pagination, setPagination] = useState({ skip: 0, limit: 20 });

    const [walletLoading] = useFetch({
        fetchFn: stores.walletStore.getCourierWalletWithDailyTransactionsSummary.bind(null, {
            courierId: courier?._id,
            startDate: selectedDateRange?.[0]?.format("YYYY-MM-DD"),
            endDate: selectedDateRange?.[1]?.format("YYYY-MM-DD"),
            skip: pagination.skip,
            limit: pagination.limit
        }),
        dependencies: [
            courier?._id, 
            selectedDateRange?.[0]?.format("YYYY-MM-DD"), 
            selectedDateRange?.[1]?.format("YYYY-MM-DD"), 
            pagination.skip, 
            pagination.limit,
            canRender
        ],
        initialCall: false,
        condition: Boolean(courier?._id) && canRender,
        callbackFunc: result => {
            setWallet(result.data);
        }
    });

    useEffect(() => {
        if (canRender && walletData && !wallet) {
            setWallet(walletData);
        }
    }, [canRender, JSON.stringify(walletData)]);

    useEffect(() => {
        resetDailyTransactions();
    }, [canRender]);

    const resetDailyTransactions = () => {
        setSelectedDailyTransactionId(null);
        setDailyDetail(null);
        setDailyDetailLoading(false);
        setDailyTransactions([]);
        setDailyTransactionsLoading(false);
    };

    const handleDateRangeChange = async dates => {
        resetDailyTransactions();
        if (!dates || !dates[0] || !dates[1]) {
            onDateRangeChange(null);
            return;
        }
        onDateRangeChange([moment(dates[0]), moment(dates[1])]);
    };

    const handleDailyTransactionSelect = dailyTransactionId => {
        setDailyDetailLoading(true);
        setDailyTransactionsLoading(true);
        const dailyTransaction = wallet?.dailyTransactions?.find(
            dailyTransaction => dailyTransaction._id === dailyTransactionId
        );
        setSelectedDailyTransactionId(dailyTransactionId);
        setSelectedDate(
            dailyTransaction?.date ? moment(dailyTransaction.date).format("YYYY-MM-DD") : null
        );

        stores.walletStore
            .getDailyTransactionsSummary({
                courierId: wallet?.externalId,
                dailyTransactionId: dailyTransactionId
            })
            .then(detail => setDailyDetail(detail.data))
            .finally(() => setDailyDetailLoading(false));

        stores.walletStore
            .getDailyTransactions({
                courierId: wallet?.externalId,
                dailyTransactionId: dailyTransactionId
            })
            .then(transactions => setDailyTransactions(transactions?.data?.transactions || []))
            .finally(() => setDailyTransactionsLoading(false));
    };

    return {
        wallet,
        walletLoading,
        dailyTransactions,
        dailyTransactionsLoading,
        dailyDetail,
        dailyDetailLoading,
        selectedDate,
        selectedDailyTransactionId,
        selectedDateRange,
        handleDateRangeChange: onDateRangeChange || handleDateRangeChange,
        handleDailyTransactionSelect,
        pagination,
        setPagination
    };
};
